"use client"

import { CloseCircleFilled, DeleteOutlined, SwapRightOutlined } from "@ant-design/icons"
import { getSiteUrl } from "@repo/shared"
import IconFont from "@repo/ui/iconfont"
import { useClickAway } from "ahooks"
import { AnimatePresence, LazyMotion } from "framer-motion"
import * as m from "framer-motion/m"
import { useLocale, useTranslations } from "next-intl"
import Link from "next/link"
import { memo, useEffect, useRef, useState } from "react"
import { Button } from "antd"

import "./index.scss"

const loadMotionFeatures = () => import("../../helpers/motionFeatures").then((module) => module.default)

// Cookie 键名
const SEARCH_HISTORY_KEY = "search_history"
// 搜索历史最大数量
const MAX_SEARCH_HISTORY = 10
// Cookie domain，设置为 .metal.com 以便所有子域名共享
const COOKIE_DOMAIN = ".metal.com"
// Cookie 过期天数（1年）
const COOKIE_EXPIRE_DAYS = 30

/**
 * 搜索和订阅按钮组件
 * 包含搜索框和订阅按钮
 */
const SearchAndSubscribe = memo(function SearchAndSubscribe() {
  const locale = useLocale()
  const t = useTranslations("HeaderComp")
  const [searchValue, setSearchValue] = useState<string>("")
  const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false)
  const [hasOpenedSearch, setHasOpenedSearch] = useState<boolean>(false)
  const [searchHistory, setSearchHistory] = useState<string[]>([])
  const searchContainerRef = useRef<HTMLDivElement>(null)

  // ========== Cookie 工具函数 ==========

  /**
   * 设置 cookie（支持跨子域名共享）
   * @param name cookie 名称
   * @param value cookie 值
   * @param days 过期天数
   */
  const setCookie = (name: string, value: string, days: number = COOKIE_EXPIRE_DAYS) => {
    if (typeof document === "undefined") return

    const date = new Date()
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
    const expires = `expires=${date.toUTCString()}`
    // 设置 domain 为 .metal.com，使所有子域名都能访问
    document.cookie = `${name}=${encodeURIComponent(value)};${expires};path=/;domain=${COOKIE_DOMAIN}`
  }

  /**
   * 获取 cookie
   * @param name cookie 名称
   * @returns cookie 值，不存在返回 null
   */
  const getCookie = (name: string): string | null => {
    if (typeof document === "undefined") return null

    const nameEQ = `${name}=`
    const cookies = document.cookie.split(";")
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i]
      if (!cookie) continue
      while (cookie.charAt(0) === " ") {
        cookie = cookie.substring(1, cookie.length)
      }
      if (cookie.indexOf(nameEQ) === 0) {
        return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length))
      }
    }
    return null
  }

  // ========== 搜索历史管理 ==========

  // 从 cookie 读取搜索历史
  useEffect(() => {
    if (typeof window !== "undefined") {
      const history = getCookie(SEARCH_HISTORY_KEY)
      if (history) {
        try {
          const parsedHistory = JSON.parse(history)
          if (Array.isArray(parsedHistory)) {
            setSearchHistory(parsedHistory)
          }
        } catch (error) {
          console.error("Failed to parse search history:", error)
        }
      }
    }
  }, [])

  // 保存搜索历史到 cookie
  const saveSearchHistory = (history: string[]) => {
    if (typeof window !== "undefined") {
      const historyJson = JSON.stringify(history)
      setCookie(SEARCH_HISTORY_KEY, historyJson)
      setSearchHistory(history)
    }
  }

  // 添加搜索历史（去重并限制数量）
  const addSearchHistory = (keyword: string) => {
    if (!keyword.trim()) return

    const trimmedKeyword = keyword.trim()
    // 移除重复项，将新关键词放在最前面，限制最大数量
    const newHistory = [trimmedKeyword, ...searchHistory.filter((item) => item !== trimmedKeyword)].slice(
      0,
      MAX_SEARCH_HISTORY
    )
    saveSearchHistory(newHistory)
  }

  // 删除单个搜索历史
  const removeSearchHistory = (keyword: string) => {
    const newHistory = searchHistory.filter((item) => item !== keyword)
    saveSearchHistory(newHistory)
  }

  // 清空所有搜索历史
  const clearAllSearchHistory = () => {
    saveSearchHistory([])
  }
  /**
   * 根据当前页面路径获取对应的 category 参数
   * @returns category 值，如果没有匹配则返回 null
   */
  const getCategoryFromCurrentPage = (): string | null => {
    if (typeof window === "undefined") return null
    const { hostname, pathname } = window.location
    // 新闻列表页及详情页（testnews.metal.com 或包含 news 的域名）
    if (hostname.includes("news")) {
      return "news"
    }

    // Price center 列表页（testwww.metal.com/en/price-center）
    if (pathname.includes("/price-center")) {
      return "price"
    }
    // Events 列表页（testwww.metal.com/events）
    if (pathname.includes("/events") || pathname.includes("/webinar")) {
      return "events"
    }

    // 公告列表及详情页（testwww.metal.com/announcements）
    if (pathname.includes("/announcements")) {
      return "announcements"
    }

    return null
  }

  // 生成搜索 URL
  const getSearchUrl = (keyword: string): string => {
    const encodedKeyword = encodeURIComponent(keyword.trim())
    const baseUrl = `${getSiteUrl("www")}/${locale}/search?keyword=${encodedKeyword}`

    // 根据当前页面路径添加对应的 category 参数
    const category = getCategoryFromCurrentPage()
    if (category) {
      return `${baseUrl}&category=${category}`
    }

    return baseUrl
  }

  // 处理搜索
  const handleSearch = () => {
    const searchKeyword = searchValue
    if (!searchKeyword.trim()) return
    // 添加搜索历史
    addSearchHistory(searchKeyword)

    // 执行搜索（对用户输入进行 URL 编码，防止特殊字符导致 URL 解析错误）
    const searchUrl = getSearchUrl(searchKeyword)
    window.open(searchUrl)

    // 清空输入框并关闭搜索框
    setSearchValue("")
    setIsSearchOpen(false)
  }

  // ========== UI 交互 ==========

  // 点击搜索图标打开搜索框
  const handleSearchIconClick = () => {
    setHasOpenedSearch(true)
    setIsSearchOpen(true)
  }

  // 关闭搜索框
  const closeSearch = () => {
    setIsSearchOpen(false)
    setSearchValue("")
  }

  // 使用 useClickAway 实现点击外部区域隐藏搜索框
  useClickAway(
    () => {
      if (isSearchOpen) {
        closeSearch()
      }
    },
    searchContainerRef,
    "mousedown"
  )

  return (
    <div className="searchAndSubscribeSection">
      <div className="searchContainerSecondary" ref={searchContainerRef}>
        <IconFont
          type="icon-sousuocu1"
          onClick={handleSearchIconClick}
          style={{
            fontSize: 20,
            color: "#fff",
          }}
        />
        {hasOpenedSearch && (
          <LazyMotion features={loadMotionFeatures} strict>
            <AnimatePresence>
              {isSearchOpen && (
                <m.div
                  key="searchWrapper"
                  className="searchWrapper"
                  initial={{ opacity: 0, scale: 0.95, y: -10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -10 }}
                  transition={{
                    duration: 0.25,
                    ease: [0.4, 0, 0.2, 1],
                    opacity: { duration: 0.2 },
                    scale: { duration: 0.25 },
                    y: { duration: 0.25 },
                  }}
                >
                  <div className="searchInputContainer">
                    <input
                      type="text"
                      value={searchValue}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
                      className="searchInputExpanded"
                      placeholder={t("common.inputPlaceholder")}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleSearch()
                        }
                      }}
                    />
                    <IconFont
                      type="icon-sousuocu1"
                      onClick={handleSearch}
                      style={{
                        fontSize: 20,
                      }}
                    />
                  </div>
                  {searchHistory.length > 0 && (
                    <div className="searchHistoryContainer">
                      <div className="searchHistoryHeader">
                        <span className="searchHistoryTitle">{t("common.SearchHistory")}</span>
                        <DeleteOutlined onClick={clearAllSearchHistory} className="searchHistoryClearAll" />
                      </div>
                      <div className="searchHistoryList">
                        {searchHistory.map((keyword) => (
                          <div key={keyword} className="searchHistoryItem">
                            <Link
                              prefetch={false}
                              target="_blank"
                              href={getSearchUrl(keyword)}
                              className="searchHistoryLink"
                              onClick={() => {
                                addSearchHistory(keyword)
                                closeSearch()
                              }}
                            >
                              {keyword}
                            </Link>
                            <CloseCircleFilled
                              onClick={() => removeSearchHistory(keyword)}
                              className="searchHistoryDelete"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </m.div>
              )}
            </AnimatePresence>
          </LazyMotion>
        )}
        <Button type="link" href={getSiteUrl("user") + "/" + locale + "/subscribe"} className="subscribeButton">
          <span>{t("common.Subscribe")}</span>
          <SwapRightOutlined />
        </Button>
      </div>
    </div>
  )
})

export { SearchAndSubscribe }
